/**
 * Look a value up in an object literal WITHOUT trusting the key.
 *
 * A `Record<string, T>` written as an object literal inherits everything on
 * `Object.prototype`, so `TABLE['constructor']` answers with a FUNCTION rather
 * than `undefined`. A function is truthy, so every fallback spelled `??`, `||`
 * or `if (!hit)` beside such a lookup silently does not fire: `t()` is handed a
 * function instead of a translation key, an antd `color` prop is handed a
 * function instead of a hex string, and a `.map()` over "the rules for this
 * program" throws.
 *
 * The failure needs no attacker in most of these places — `apiGet<T>` is an
 * unchecked cast with no runtime validation, so any of these keys is whatever
 * the response held. It needs one in exactly one place, and that is where this
 * was found for the third time: `PosIntegrationSettings` indexed its OAuth
 * error table with `?posError=`, straight off the URL.
 *
 * One definition, imported everywhere, rather than the guard being re-derived
 * per call site — `features/pos/runLabels.ts` and `features/pos/syncStatus.ts`
 * each grew their own copy before this file existed, which is the drift shape
 * this codebase keeps paying for.
 *
 * Returns `undefined` for a miss and takes NO fallback parameter on purpose:
 * every call site already had its own fallback, in its own idiom, and the
 * honest change is to make those idioms correct rather than to replace them
 * with a fourth one.
 */
export function lookupOwn<T>(
  table: Record<string, T>,
  key: string | null | undefined,
): T | undefined {
  return typeof key === 'string' && Object.hasOwn(table, key) ? table[key] : undefined;
}
